import { useTranslation } from "@/contexts/LanguageContext";
import {
  Aperture,
  Timer,
  FilmStrip,
  Sparkle,
  Camera,
  Lightbulb,
  Backpack,
  SlidersHorizontal,
  Lightning,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function SettingCard({ label, value, icon, testId }) {
  return (
    <motion.div
      variants={item}
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 text-center hover:border-white/20 transition-colors"
    >
      <div className="flex items-center justify-center gap-1.5 mb-2">
        {icon}
        <span className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold">
          {label}
        </span>
      </div>
      <div
        className="font-['JetBrains_Mono'] text-2xl sm:text-3xl font-medium text-white tracking-tighter setting-value-glow"
        data-testid={testId}
      >
        {value || "-"}
      </div>
    </motion.div>
  );
}

export default function AIResultsDisplay({ result, loading, onApplyToManual }) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-12 flex flex-col items-center justify-center min-h-[400px] text-center">
        <Sparkle weight="fill" className="w-16 h-16 text-[#F5A524] mb-4 animate-pulse" />
        <h3 className="font-['Outfit'] text-xl text-white mb-2" data-testid="ai-loading-title">
          {t("ai_generating")}
        </h3>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-12 flex flex-col items-center justify-center min-h-[400px] text-center">
        <Sparkle weight="thin" className="w-16 h-16 text-[#242429] mb-4" />
        <h3 className="font-['Outfit'] text-xl text-white mb-2" data-testid="ai-results-empty-title">
          {t("ai_empty_title")}
        </h3>
        <p className="text-[#71717A] max-w-sm">{t("ai_empty")}</p>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={JSON.stringify(result)}
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-4"
        data-testid="ai-results-display"
      >
        <motion.div variants={item}>
          <h2 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
            <Sparkle weight="fill" className="w-5 h-5 text-[#F5A524]" />
            {t("ai_results_title")}
          </h2>
        </motion.div>

        {/* Scenario */}
        {result.scene_summary && (
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4"
          >
            <span className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold">
              {t("ai_scene")}
            </span>
            <p className="text-[#E4E4E7] text-sm mt-1" data-testid="ai-scene-summary">
              {result.scene_summary}
            </p>
          </motion.div>
        )}

        {/* Main settings */}
        <div className="grid grid-cols-3 gap-3">
          <SettingCard
            label={t("res_aperture")}
            value={result.aperture}
            icon={<Aperture weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="ai-aperture-result"
          />
          <SettingCard
            label={t("res_shutter")}
            value={result.shutter_speed}
            icon={<Timer weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="ai-shutter-result"
          />
          <SettingCard
            label={t("res_iso")}
            value={result.iso}
            icon={<FilmStrip weight="regular" className="w-3.5 h-3.5 text-[#F5A524]" />}
            testId="ai-iso-result"
          />
        </div>

        {/* Recommended lens */}
        {result.recommended_lens && (
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Camera weight="regular" className="w-4 h-4 text-[#F5A524]" />
                <h4 className="text-white font-medium text-sm">{t("ai_lens_label")}</h4>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-md font-medium flex items-center gap-1 ${
                  result.used_gear
                    ? "bg-[#F5A524]/10 text-[#F5A524]"
                    : "bg-white/5 text-[#A1A1AA]"
                }`}
                data-testid="ai-lens-source-badge"
              >
                {result.used_gear && <Backpack weight="fill" className="w-3 h-3" />}
                {result.used_gear ? t("ai_from_your_gear") : t("ai_suggested_lens")}
              </span>
            </div>
            <p className="text-[#E4E4E7] text-sm font-medium" data-testid="ai-recommended-lens">
              {result.recommended_lens}
            </p>
            {result.recommended_lens_reason && (
              <p className="text-[#A1A1AA] text-sm mt-1 leading-relaxed">
                {result.recommended_lens_reason}
              </p>
            )}
          </motion.div>
        )}

        {/* Lighting setup */}
        {result.lighting_setup && result.lighting_setup.relevant && result.lighting_setup.items.length > 0 && (
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5"
            data-testid="ai-lighting-section"
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Lightning weight="regular" className="w-4 h-4 text-[#F5A524]" />
                <h4 className="text-white font-medium text-sm">{t("ai_lighting_title")}</h4>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-md font-medium bg-white/5 text-[#A1A1AA]" data-testid="ai-lighting-badge">
                {t("ai_lighting_badge")}
              </span>
            </div>
            {result.lighting_setup.note && (
              <p className="text-[#A1A1AA] text-sm mb-3 leading-relaxed" data-testid="ai-lighting-note">
                {result.lighting_setup.note}
              </p>
            )}
            <div className="space-y-2">
              {result.lighting_setup.items.map((li, i) => (
                <div key={i} className="flex items-start gap-2.5" data-testid={`ai-lighting-item-${i}`}>
                  <Lightbulb weight="fill" className="w-3.5 h-3.5 text-[#F5A524] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[#E4E4E7] text-sm font-medium">{li.name}</span>
                    {li.why && <span className="text-[#A1A1AA] text-sm"> — {li.why}</span>}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/lighting-basics"
              className="inline-flex items-center gap-1 text-[#F5A524] text-xs font-medium hover:underline mt-3"
              data-testid="ai-lighting-learn-link"
            >
              {t("ai_lighting_learn")} →
            </Link>
          </motion.div>
        )}

        {/* Pro tips */}
        {result.pro_tips && result.pro_tips.length > 0 && (
          <motion.div
            variants={item}
            className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/30 rounded-xl p-5"
          >
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb weight="fill" className="w-4 h-4 text-[#F5A524]" />
              <h4 className="text-[#F5A524] font-medium text-sm">{t("ai_protips")}</h4>
            </div>
            <div className="space-y-2">
              {result.pro_tips.map((tip, i) => (
                <p
                  key={i}
                  className="text-[#A1A1AA] text-sm flex items-start gap-2"
                  data-testid={`ai-pro-tip-${i}`}
                >
                  <span className="text-[#F5A524] mt-0.5 shrink-0">{i + 1}.</span>
                  {tip}
                </p>
              ))}
            </div>
          </motion.div>
        )}

        {/* Apply to Manual form */}
        {result.manual_form && onApplyToManual && (
          <motion.div variants={item}>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={onApplyToManual}
                    variant="outline"
                    className="w-full border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524]/10 hover:border-[#F5A524]/60"
                    data-testid="ai-apply-to-manual-button"
                  >
                    <SlidersHorizontal weight="regular" className="w-4 h-4 mr-2" />
                    {t("ai_apply")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs bg-[#18181B] text-[#E4E4E7] border border-white/10 text-xs leading-relaxed"
                  data-testid="ai-apply-tooltip"
                >
                  {t("ai_apply_tooltip")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <p className="text-[#52525B] text-xs text-center mt-2" data-testid="ai-apply-hint">
              {t("ai_apply_hint")}
            </p>
          </motion.div>
        )}

        <motion.p
          variants={item}
          className="text-[#52525B] text-xs text-center"
          data-testid="ai-disclaimer"
        >
          {t("ai_disclaimer")}
        </motion.p>
      </motion.div>
    </AnimatePresence>
  );
}
